import { flagsFromMe } from '@codex/contracts';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { requireSession } from '../../auth/require-session.js';
import { SessionError, SessionPending } from '../../auth/SessionStates.js';
import { RunPage } from '../../run/run-page.js';
import { parseRunSearch } from '../../run/search.js';
import { NotFound } from '../../shell/not-found.js';

/**
 * `/run/:sessionId` (2.E deliverable 4): Run mode, guarded as every authenticated route is, the
 * selected item in `?item=` (replaced, not pushed, as the selection moves). With the `sessions`
 * flag off it is the not-found page.
 */
export const Route = createFileRoute('/run/$sessionId')({
  staticData: { crumb: 'Run' },
  validateSearch: parseRunSearch,
  beforeLoad: async ({ location }) => ({ me: await requireSession(location.pathname) }),
  pendingComponent: SessionPending,
  pendingMs: 0,
  pendingMinMs: 0,
  errorComponent: SessionError,
  component: RunRoute,
});

function RunRoute(): React.JSX.Element {
  const { me } = Route.useRouteContext();
  const { sessionId } = Route.useParams();
  const { item } = Route.useSearch();
  const navigate = Route.useNavigate();
  // A selection change replaces `?item=` and keeps every scroll: the router's default reset
  // would scroll the window back to the top after the selected row scrolled itself into view.
  const onItemChange = useCallback(
    (next: string) => void navigate({ search: { item: next }, replace: true, resetScroll: false }),
    [navigate],
  );
  if (!flagsFromMe(me).sessions) return <NotFound />;
  return (
    <RunPage key={sessionId} sessionId={sessionId} itemFromUrl={item} onItemChange={onItemChange} />
  );
}
